class FacilitySettingsService {

    constructor(
        $http,
        appConfig
    ) {
        'ngInject';

        this.$http = $http;
        this.appConfig = appConfig;

        this.fields = undefined;
        this.facilityFieldMap = {};
        this.selectedRelatedModels = {};
        this.previousSettings = {};
        this.relatedModels = undefined;

        this.daysFieldName = 'days_of_operation';

        // The properties of each setting that is used to display the selected value. This is necessary for setting up
        // the dropdown selectors.
        this.displayProps = {
            facility_manager: 'email_addr',
            staffing_model: 'name',
            payment_method: 'name',
            point_of_sale_software: 'name',
            membership_software: 'name',
            membership_types: 'name',
            site_type: 'name',
        };

        // Shamelessly copied from https://stackoverflow.com/a/16699507/1524640
        this.phonePattern = '^(\\+\\d{1,2}\\s)?\\(?\\d{3}\\)?[\\s.-]\\d{3}[\\s.-]\\d{4}$';

        // Localization values initialized with default values
        this.units = {
            AREA: {
                symbol: '',
                name: 'Sq Ft'
            },
            CURRENCY: {
                symbol: '$',
                name: 'Dollars'
            }
        };

    }

    configureFacilityFieldMap(fields) {
        for (let field of fields) {
            this.facilityFieldMap[field.name] = field;
        }
    }

    /***
     * Does some configuring and reorganizing of the Facility Settings data, including:
     * 1) translates the boolean list of Days of Operation into a list of Days object IDs
     * 2) stores a backup copy of the existing settings to track which values were changed
     */
    configureSettings() {
        this.previousSettings = angular.copy(this.facilitySettings);
    }

    /***
     * setLocalizationUnits
     *  Sets the localization units to be used in facility forms.
     *   -Triggered when a month is selected from the stats form, afterfacility/location/client are selected.
     * @param currentFacility {object}  An object containing the config for the currently selected facility
     */
    setLocalizationUnits(currentFacility) {
        this.units.CURRENCY.symbol = currentFacility.currency_symbol;
        this.units.CURRENCY.name = currentFacility.currency;
        this.units.AREA.name = currentFacility.size_unit;
    }


    /***
     * Generates an object that maps a FacilitySetting field name to the selected object from the database. Because we
     * only get the currently saved value as an ID of an object, we need to find the appropriate list of related models
     * and then filter them down to find the one that matches the saved ID.
     *
     * Currently, this only applies to DROPDOWN_RELATED inputs.
     *
     * @param fields                a list of FacilitySetting fields from the API
     * @param relatedModelList      an object mapping related_model names to a list of corresponding objects
     * @param facilitySettings      an object mapping a FacilitySetting "name" field to its saved value from the API
     *
     * @returns {object}
     */
    populateSelectedRelatedModels(fields, relatedModelList, facilitySettings) {
        let toReturn = {};
        for (let field of fields) {
            if (field.display_field === 'DROPDOWN_RELATED' && relatedModelList[field.related_model]) {
                toReturn[field.name] = relatedModelList[field.related_model]
                                    .filter((m) => m.id === facilitySettings[field.name]).pop();
            }
        }
        return toReturn;
    }

    /***
     * For all of the fields that use a list of checkboxes that correspond to foreign keys, translate that list of IDs
     * into isChecked boolean properties on each item so that the checkboxes rendered can properly display the correct
     * saved vales.
     *
     * @param fields                a list of FacilitySetting fields from the API
     * @param relatedModelList      an object mapping related_model names to a list of corresponding objects
     * @param facilitySettings      an object mapping a FacilitySetting "name" field to its saved value from the API
     */
    populateSelectedCheckboxItems(fields, relatedModelList, facilitySettings) {
        for (let field of fields) {
            if (facilitySettings[field.name] !== undefined
                && (field.display_field === 'MULTIPLE' || field.display_field === 'DAYS_OF_WEEK')) {
                relatedModelList[field.related_model]
                    .map((m) => m.isChecked = facilitySettings[field.name].includes(m.id));
            }
        }
    }

    /***
     * Parse through the currently set values in the facility edit form and generate an object that contains only the
     * names and values of fields that were updated. This ensure that we only commit meaningful changes to the database,
     * rather than doing a broad save of every setting each time.
     *
     * In a few places, we also update newSettings to ensure that, if the save completes successfully, we have the
     * properly formatted values saved to the object so future saves will use that as the new baseline when determining
     * whether any values have been saved.
     *
     * @param fields                    an array of FacilitySetting field objects that each define the metadata for a
     *                                  form field.
     * @param newSettings               an object mapping a FacilitySetting name to its value. This keeps and tracks
     *                                  most of the new values as they are edited in the UI.
     * @param oldSettings               an object mapping a FacilitySetting name to its value. This is a copy of
     *                                  newSettings that is generated each time a Facility is loaded into the edit
     *                                  panel.
     * @param selectedRelatedModels     an object mapping related model names to an object indicating which model was
     *                                  selected. e.g. {User: {id: 1, email_addr: 'test@email.org'...}}
     * @param forceAllFields            a boolean determining whether or not to force all fields to be included in the
     *                                  returned object. If true, all fields are included. If false, only values that
     *                                  have changed are returned.
     */
    getSettingsToSave(fields, newSettings, oldSettings, selectedRelatedModels, forceAllFields) {
        forceAllFields = forceAllFields === undefined ? false : forceAllFields;
        let settingsToSave = {};
        for (let field of this.fields) {
            let relatedModel = this.relatedModels[field.related_model];
            switch (field.display_field) {
                case 'DROPDOWN_RELATED':
                    // Grab the ID of the related item that was selected
                    let selectedRelatedModel = selectedRelatedModels[field.name];
                    if (selectedRelatedModel !== undefined
                        && (forceAllFields || (oldSettings[field.name] !== selectedRelatedModel.id))) {
                        settingsToSave[field.name] = selectedRelatedModel.id;
                        newSettings[field.name] = selectedRelatedModel.id;
                    }
                    break;
                case 'MULTIPLE':
                    // Filter down the array of related items to the IDs of those that are checked.
                    let selectedItems = undefined;
                    if (relatedModel !== undefined) {
                        selectedItems = relatedModel.filter((m) => m.isChecked).map((m) => m.id);
                    }
                    if (forceAllFields || (String(oldSettings[field.name]) !== String(selectedItems))) {
                        // Convert the array of IDs into a comma-separated string of IDs;
                        if (selectedItems.length > 0) {
                            settingsToSave[field.name] = String(selectedItems.reduce((acc, m) => `${acc},${m}`));
                            newSettings[field.name] = selectedItems;
                        }
                    }
                    break;
                default:
                    // Otherwise, the value is an input field that automatically updates its values and we can directly
                    // evaluate whether or not it has changed.
                    if (forceAllFields || (newSettings[field.name] !== oldSettings[field.name])) {
                        settingsToSave[field.name] = newSettings[field.name]
                    }
            }
        }

        return settingsToSave;
    }


    /***
     * Fetch all of the FacilitySettings from the API. These represent the metadata about the various form fields that
     * are rendered on the Facility Settings page.
     *
     * If the FacilitySettings have already been fetched once, then we return the locally stored version, since they
     * are unlikely to change.
     *
     * @returns {Promise | array}
     */
    getFacilityFields() {
        if (this.fields !== undefined) {
            return this.fields;
        }
        return this.$http.get(`${this.appConfig.apiUrl}/facilities/fields/`)
            .then((successResponse) => {
                this.fields = successResponse.data;
                this.configureFacilityFieldMap(this.fields);
                return this.fields;
            });
    }

    /***
     * Fetch the latest FacilitySettingValues for the provided client. The data is returned as a JSON object mapping
     * field names to their value.
     *
     * @param facilityId        the ID of the facility whose settings are being fetched
     * @param month             the month representing the effective date of the settings being fetched
     * @param year              the year representing the effective date of the settings being fetched
     *
     * @returns {object}
     */
    getFacilitySettings(facilityId, month, year) {
        const d = new Date();
        month = month === undefined ? d.getMonth() + 1 : month;
        year = year === undefined ? d.getFullYear() : year;
        const config = {
            params: {
                month: month,
                year: year
            }
        };
        return this.$http.get(`${this.appConfig.apiUrl}/facilities/${facilityId}/settings/`, config)
            .then((successResponse) =>  {
                this.facilitySettings = successResponse.data;
                this.configureSettings();
                return this.facilitySettings;
            });
    }

    /***
     * Get all of the related models that are used for dropdowns and checkboxes on the client edit page.
     */
    getRelatedModels() {
        if (this.relatedModels !== undefined) {
            return this.relatedModels;
        };
        return this.$http.get(`${this.appConfig.apiUrl}/facilities/related-fields/`)
            .then((successResponse) =>  {
                this.relatedModels = successResponse.data;
                return this.relatedModels;
            });
    }

    /***
     * Save the updated Facility Settings for the given facility.
     *
     * @param facilityId        the ID of the facility being updated.
     * @param data              an object containing 3 keys:
     *                          month - the month for the effective date of the data being saved
     *                          year - the year for the effective date of the data being saved
     *                          name (optional) - an updated name for this Facility
     *                          facility_setting_values - all of the updated values for this Facility.
     *
     * @returns {Promise}
     */
    updateFacilitySettings(facilityId, data) {
        return this.$http.post(`${this.appConfig.apiUrl}/facilities/${facilityId}/settings/`, data);
    }

}

export default FacilitySettingsService;


// WEBPACK FOOTER //
// ./app/common/facility-settings/facility-settings-service.js